import { useEffect, useRef, useState } from 'react'

/* Single-fire IntersectionObserver — returns [ref, hasTriggered] */
function useOnView(threshold = 0.35) {
  const ref = useRef(null)
  const [on, setOn] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setOn(true); e.target.classList.add('is-visible'); obs.disconnect() } },
      { threshold }
    )
    obs.observe(el)
    // failsafe: if the observer never fires (hidden tab, odd engine states),
    // the section still reveals instead of holding opacity 0 forever
    const failsafe = setTimeout(() => { el.classList.add('is-visible'); obs.disconnect() }, 2600)
    return () => { obs.disconnect(); clearTimeout(failsafe) }
  }, [threshold])
  return [ref, on]
}

/* Reveal hook for text blocks */
function useReveal(threshold = 0.15) {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('is-visible'); obs.disconnect() } },
      { threshold }
    )
    obs.observe(el)
    // failsafe: if the observer never fires (hidden tab, odd engine states),
    // the section still reveals instead of holding opacity 0 forever
    const failsafe = setTimeout(() => { el.classList.add('is-visible'); obs.disconnect() }, 2600)
    return () => { obs.disconnect(); clearTimeout(failsafe) }
  }, [threshold])
  return ref
}

/* -----------------------------------------------------------------------
   Chart 1 — R$50M
   Rising area / line that draws from left to right when on=true
------------------------------------------------------------------------ */
function SavingsChart({ on }) {
  const linePath =
    'M0,62 C30,56 55,46 80,38 C105,30 130,18 160,10 C175,6 188,3 200,1'
  const areaPath =
    'M0,62 C30,56 55,46 80,38 C105,30 130,18 160,10 C175,6 188,3 200,1 L200,68 L0,68 Z'
  const pathLen = 228 // >= actual length

  return (
    <svg viewBox="0 0 200 68" className="w-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id="sg-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(201,211,58,0.32)" />
          <stop offset="100%" stopColor="rgba(201,211,58,0.00)" />
        </linearGradient>
      </defs>

      {/* Area fades in after line finishes */}
      <path
        d={areaPath}
        fill="url(#sg-fill)"
        style={{
          opacity: on ? 1 : 0,
          transition: 'opacity 0.8s ease 1.8s',
        }}
      />

      {/* Line draws from left to right */}
      <path
        d={linePath}
        fill="none"
        stroke="rgba(201,211,58,0.80)"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeDasharray={pathLen}
        strokeDashoffset={on ? 0 : pathLen}
        style={{ transition: 'stroke-dashoffset 2s cubic-bezier(0.4,0,0.2,1)' }}
      />

      {/* Baseline */}
      <line x1="0" y1="67" x2="200" y2="67" stroke="rgba(201,211,58,0.10)" strokeWidth="1" />

      {/* Pulsing endpoint — appears when line finishes */}
      <circle
        cx="200" cy="1" r="3.5"
        fill="#c9d33a"
        style={{ opacity: on ? 1 : 0, transition: 'opacity 0.3s ease 2s' }}
      />
      {on && (
        <circle cx="200" cy="1" r="3.5" fill="#c9d33a">
          <animate attributeName="r"       values="3.5;9;3.5"   dur="2.8s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.5;0;0.5"   dur="2.8s" repeatCount="indefinite" />
        </circle>
      )}
    </svg>
  )
}

/* -----------------------------------------------------------------------
   Chart 2 — 80%
   Arc ring that fills from 0 to 80% when on=true
------------------------------------------------------------------------ */
function ReductionRing({ on }) {
  const R = 72
  const C = +(2 * Math.PI * R).toFixed(2)
  const full80 = +(C * 0.80).toFixed(2)
  const gap80  = +(C * 0.20).toFixed(2)

  return (
    <svg viewBox="0 0 200 200" className="w-full max-w-[200px] mx-auto block">
      {/* Outer glow track */}
      <circle
        cx="100" cy="100" r={R}
        fill="none"
        stroke="rgba(248,129,22,0.08)"
        strokeWidth="14"
      />

      {/* Arc fills 0 → 80% */}
      <circle
        cx="100" cy="100" r={R}
        fill="none"
        stroke="rgba(248,129,22,0.88)"
        strokeWidth="14"
        strokeLinecap="round"
        strokeDasharray={on ? `${full80} ${gap80}` : `0 ${C}`}
        transform="rotate(-90 100 100)"
        style={{
          transition: 'stroke-dasharray 1.8s cubic-bezier(0.4,0,0.2,1) 0.3s',
          filter: 'drop-shadow(0 0 6px rgba(248,129,22,0.45))',
        }}
      />

      {/* Center labels */}
      <text x="100" y="93" textAnchor="middle" fontFamily="inherit"
        fill="rgba(248,129,22,0.90)" fontSize="26" fontWeight="300">80%</text>
      <text x="100" y="116" textAnchor="middle" fontFamily="inherit"
        fill="rgba(255,255,255,0.30)" fontSize="11">tickets reduced</text>
    </svg>
  )
}

/* -----------------------------------------------------------------------
   Chart 3 — 10+ years
   Horizontal timeline: line extends, dots light up one by one
------------------------------------------------------------------------ */
function TimelineChart({ on }) {
  const DOTS   = 11            // Year 0 through Year 10+
  const W      = 200
  const y      = 26            // vertical midline
  const x0     = 10
  const x1     = W - 10
  const step   = (x1 - x0) / (DOTS - 1)

  // Labels shown at specific positions. The span tracks the practice back to
  // 1994, so it has to match the 30+ figure above the chart.
  const labels = {
    0:  '1994',
    3:  'Yr 10',
    6:  'Yr 20',
    10: '30+',
  }

  return (
    <svg viewBox={`0 0 ${W} 62`} className="w-full" preserveAspectRatio="none">
      {/* Dim track */}
      <line
        x1={x0} y1={y} x2={x1} y2={y}
        stroke="rgba(25,162,207,0.12)"
        strokeWidth="1.5"
      />

      {/* Animated progress line draws left to right */}
      <line
        x1={x0} y1={y} x2={x1} y2={y}
        stroke="rgba(25,162,207,0.65)"
        strokeWidth="1.5"
        strokeDasharray={x1 - x0}
        strokeDashoffset={on ? 0 : x1 - x0}
        style={{ transition: 'stroke-dashoffset 2.5s cubic-bezier(0.4,0,0.2,1)' }}
      />

      {/* Year dots — staggered fade-in */}
      {Array.from({ length: DOTS }, (_, i) => {
        const cx     = x0 + i * step
        const isLast = i === DOTS - 1
        const delay  = 0.15 + i * 0.2
        const r      = isLast ? 5 : 3.5
        const fill   = isLast
          ? 'rgba(25,162,207,0.95)'
          : `rgba(25,162,207,${0.35 + i * 0.055})`

        return (
          <g key={i}>
            {/* Dot */}
            <circle
              cx={cx} cy={y} r={r}
              fill={fill}
              style={{
                opacity: on ? 1 : 0,
                transition: `opacity 0.35s ease ${delay}s`,
              }}
            />
            {/* Pulse on last dot */}
            {isLast && on && (
              <circle cx={cx} cy={y} r={5} fill="rgba(25,162,207,0.35)">
                <animate attributeName="r"       values="5;13;5"   dur="3s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.35;0;0.35" dur="3s" repeatCount="indefinite" />
              </circle>
            )}
          </g>
        )
      })}

      {/* Year labels */}
      {Object.entries(labels).map(([iStr, label]) => {
        const i     = Number(iStr)
        const cx    = x0 + i * step
        const delay = 0.15 + i * 0.2

        return (
          <text
            key={label}
            x={cx} y={y + 22}
            textAnchor="middle"
            fontFamily="inherit"
            fontSize="11"
            fontWeight="500"
            fill="rgba(25,162,207,0.70)"
            style={{
              opacity: on ? 1 : 0,
              transition: `opacity 0.35s ease ${delay + 0.1}s`,
            }}
          >
            {label}
          </text>
        )
      })}
    </svg>
  )
}

/* -----------------------------------------------------------------------
   Chart 4 — 3,000+ people trained
   A unit chart: rows of people fill in one by one when on=true
------------------------------------------------------------------------ */
function PeopleChart({ on }) {
  const COLS = 12
  const ROWS = 3
  const W    = 200
  const stepX = W / COLS
  const stepY = 19
  const y0    = 12

  return (
    <svg viewBox="0 0 200 62" className="w-full" preserveAspectRatio="none">
      {Array.from({ length: ROWS }, (_, r) =>
        Array.from({ length: COLS }, (_, c) => {
          const i     = r * COLS + c
          const cx    = stepX / 2 + c * stepX
          const cy    = y0 + r * stepY
          // fill left to right, row by row, so it reads as a growing cohort
          const delay = 0.15 + i * 0.045

          return (
            <g
              key={i}
              style={{
                opacity: on ? 1 : 0,
                transition: `opacity 0.3s ease ${delay}s`,
              }}
            >
              <circle cx={cx} cy={cy - 3.6} r="2" fill="rgba(222,175,42,0.85)" />
              <path
                d={`M${cx - 3.2} ${cy + 3.4} a 3.2 3.2 0 0 1 6.4 0`}
                fill="none"
                stroke="rgba(222,175,42,0.85)"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </g>
          )
        })
      )}
    </svg>
  )
}

/* -----------------------------------------------------------------------
   Section
------------------------------------------------------------------------ */

const CHARTS  = [SavingsChart, ReductionRing, TimelineChart, PeopleChart]
const COLORS  = ['text-pyramid-1',   'text-pyramid-10',   'text-pyramid-19',  'text-pyramid-5']
const DIVIDERS = ['bg-pyramid-1/25', 'bg-pyramid-10/25',  'bg-pyramid-19/25', 'bg-pyramid-5/25']

const FoundationSection = ({ content }) => {
  const headRef             = useReveal(0.15)
  const [gridRef, chartsOn] = useOnView(0.30)

  return (
    <section className="relative bg-transparent px-6 py-24 md:px-16 overflow-hidden">
      {/* Accent blob */}
      <div
        className="absolute top-0 right-0 w-80 h-80 rounded-full opacity-10 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(201,211,58,0.6) 0%, transparent 70%)',
          filter: 'blur(70px)',
          transform: 'translate(35%, -35%)',
        }}
      />

      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div ref={headRef} className="reveal-on-scroll">
          <p className="text-xs uppercase tracking-[0.4em] text-pyramid-1/75">
            {content.eyebrow}
          </p>
          <h2 className="mt-4 max-w-2xl text-3xl font-light text-white md:text-4xl leading-tight">
            {content.headline}
          </h2>
          <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-300">
            {content.body}
          </p>
        </div>

        {/* Stat cards — chartsOn fires all three charts */}
        <div
          ref={gridRef}
          className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 stagger-grid"
        >
          {content.credentials.map((cred, i) => {
            const Chart = CHARTS[i]
            return (
              <div
                key={cred.metric}
                className="stagger-card stat-card rounded-2xl p-7 text-left flex flex-col"
                style={{ '--card-delay': `${i * 140}ms` }}
              >
                {/* Big number — color matches the chart */}
                <div className={`text-5xl font-light tracking-tight leading-none ${COLORS[i]}`}>
                  <div>{cred.metric}</div>
                  {cred.unit && <div>{cred.unit}</div>}
                </div>
                <div className={`mt-3 h-px w-10 ${DIVIDERS[i]}`} />

                {/* Chart */}
                <div className="mt-5 flex-1 min-h-[100px] flex items-end">
                  <div className="w-full">
                    <Chart on={chartsOn} />
                  </div>
                </div>

                {/* Label */}
                <p className="mt-4 text-sm leading-6 text-slate-400">{cred.label}</p>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default FoundationSection
